import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateWebPageSchema, generateWebSiteSchema } from '../../utils/seo';

const SERVICES_ACCORDION = [
  {
    title: 'Vehicle Registration',
    items: [
      { label: 'New Vehicle Registration', to: '/vehicle-registration' },
      { label: 'Registration Renewal', to: '/nevada-registration-renewal' },
      { label: 'Out-of-State Transfer', to: '/new-resident' },
      { label: 'Registration Fee Guide', to: '/nevada-registration-fees' },
      { label: 'Temporary Permits', to: '/temporary-permits' },
    ],
    link: { label: 'All Registration Services', to: '/vehicle-registration' },
  },
  {
    title: 'Title Services',
    items: [
      { label: 'Title Transfer', to: '/title-transfer' },
      { label: 'Duplicate Title', to: '/duplicate-title' },
      { label: 'Lien Release', to: '/nevada-lien-release' },
      { label: 'Salvage Title', to: '/nevada-salvage-title' },
      { label: 'Title After Death', to: '/nevada-title-after-death' },
    ],
    link: { label: 'All Title Services', to: '/title-transfer' },
  },
  {
    title: 'License Plates',
    items: [
      { label: 'Standard Plates', to: '/license-plates' },
      { label: 'Specialty Plates', to: '/nevada-specialty-plates' },
      { label: 'Personalized Plates', to: '/license-plates' },
      { label: 'License Plate Guide', to: '/nevada-license-plates-guide' },
    ],
    link: { label: 'All Plate Services', to: '/license-plates' },
  },
  {
    title: 'Notary & Documents',
    items: [
      { label: 'Notary Services', to: '/notary-services' },
      { label: 'Power of Attorney', to: '/nevada-power-of-attorney-vehicle' },
      { label: 'Required Documents', to: '/required-documents' },
      { label: 'Nevada DMV Forms', to: '/nevada-dmv-forms' },
    ],
    link: { label: 'All Document Services', to: '/notary-services' },
  },
  {
    title: 'Smog & Inspections',
    items: [
      { label: 'Smog & Emissions Guide', to: '/nevada-smog-emissions-guide' },
      { label: 'VIN Inspection', to: '/nevada-vin-inspection' },
      { label: 'OHV Registration', to: '/nevada-ohv-registration' },
    ],
    link: { label: 'Smog & Inspection Info', to: '/nevada-smog-emissions-guide' },
  },
  {
    title: 'New to Nevada',
    items: [
      { label: 'New Resident Guide', to: '/new-resident' },
      { label: 'New Resident Registration', to: '/nevada-new-resident-registration' },
      { label: 'Driver\'s License Guide', to: '/nevada-drivers-license-guide' },
      { label: 'REAL ID Information', to: '/nevada-real-id' },
    ],
    link: { label: 'New Resident Resources', to: '/new-resident' },
  },
  {
    title: 'Commercial Vehicles',
    items: [
      { label: 'Commercial Vehicle Info', to: '/nevada-commercial-vehicle' },
      { label: 'Trailer Registration', to: '/nevada-trailer-registration' },
      { label: 'Dealer & Fleet Services', to: '/services' },
    ],
    link: { label: 'Commercial Services', to: '/nevada-commercial-vehicle' },
  },
  {
    title: 'Fees & Penalties',
    items: [
      { label: 'Registration Fees', to: '/nevada-registration-fees' },
      { label: 'Late Penalties', to: '/nevada-late-registration-penalties' },
      { label: 'Service Fee Schedule', to: '/fees' },
    ],
    link: { label: 'View All Fees', to: '/fees' },
  },
];

const TOPIC_CARDS = [
  {
    title: 'Vehicle Registration',
    desc: 'New registrations, renewals, out-of-state transfers, and temporary permits for all Nevada vehicles.',
    items: ['New Registration', 'Renewal', 'Out-of-State Transfer'],
    link: { label: 'All Registration Services', to: '/vehicle-registration' },
    icon: 'ri-car-line',
  },
  {
    title: 'Title Transfers',
    desc: 'Private party sales, dealer purchases, gift transfers, lien releases, and duplicate titles.',
    items: ['Private Party Sale', 'Lien Release', 'Duplicate Title'],
    link: { label: 'All Title Services', to: '/title-transfer' },
    icon: 'ri-file-transfer-line',
  },
  {
    title: 'New to Nevada?',
    desc: 'Welcome to the Silver State! Get your vehicles registered and learn what you need as a new resident.',
    items: ['New Resident Guide', 'Driver\'s License Info', 'REAL ID'],
    link: { label: 'New Resident Resources', to: '/new-resident' },
    icon: 'ri-map-pin-line',
  },
  {
    title: 'License Plates',
    desc: 'Standard, specialty, personalized, and veteran license plates for Nevada vehicles.',
    items: ['Specialty Plates', 'Personalized Plates', 'Plate Renewal'],
    link: { label: 'License Plate Services', to: '/license-plates' },
    icon: 'ri-price-tag-3-line',
  },
  {
    title: 'Notary Services',
    desc: 'Certified notarization for titles, bills of sale, power of attorney, and DMV affidavits.',
    items: ['Title Notarization', 'Power of Attorney', 'Bill of Sale'],
    link: { label: 'Notary Services', to: '/notary-services' },
    icon: 'ri-quill-pen-line',
  },
  {
    title: 'Dealer & Fleet',
    desc: 'High-volume processing for dealerships and businesses. Dedicated account management available.',
    items: ['Dealer Registration', 'Fleet Renewals', 'Bulk Processing'],
    link: { label: 'Dealer Services', to: '/services' },
    icon: 'ri-store-2-line',
  },
];

const TESTIMONIALS = [
  { name: 'Sarah M.', location: 'Henderson, NV', text: 'Saved me 3 hours at the DMV! The team handled my title transfer in under 30 minutes. Professional service and very fair pricing. I will never go to the actual DMV again.', rating: 5 },
  { name: 'Carlos R.', location: 'Las Vegas, NV', text: 'Just moved to Nevada and had no idea about the registration process. Speedy DMV walked me through everything and had my car registered the same day. Couldn\'t be happier.', rating: 5 },
  { name: 'Jennifer T.', location: 'North Las Vegas, NV', text: 'Needed a duplicate title and was dreading the whole process. They took care of everything in one visit. Highly recommended to anyone in the Las Vegas Valley.', rating: 5 },
  { name: 'Mike D.', location: 'Summerlin, NV', text: 'I run a small car lot and Speedy DMV handles all our registration and title work. Super professional and they know Nevada DMV rules inside and out. Best in the business!', rating: 5 },
];

export default function HomePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';
  const [openAccordions, setOpenAccordions] = useState<number[]>([0, 1]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useSEO({
    title: 'Speedy DMV Services Las Vegas Nevada | Licensed 3rd Party DMV Provider',
    description: 'Skip the Nevada DMV lines! Speedy DMV Services in Las Vegas — licensed 3rd party provider for vehicle registration, title transfer, license plates & notary services. Call 702.979.7202.',
    keywords: 'DMV services Las Vegas Nevada, vehicle registration Las Vegas, title transfer Las Vegas NV, Nevada DMV third party, license plates Las Vegas, notary services Nevada',
    canonical: siteUrl,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateWebSiteSchema(),
        generateWebPageSchema(
          'Speedy DMV Services Las Vegas — Licensed 3rd Party Nevada DMV Provider',
          'Licensed 3rd party Nevada DMV service provider in Las Vegas. Fast vehicle registration, title transfers, license plates, and notary services.',
          siteUrl
        )
      ]
    }
  });

  useEffect(() => {
    const interval = setInterval(() => setActiveTestimonial(p => (p + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const toggleAccordion = (i: number) => {
    setOpenAccordions(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
  };

  const zohoServices = ['Vehicle Registration', 'Title Transfer', 'License Plates', 'Duplicate Title', 'Temporary Permits', 'Notary Services', 'Dealer / Fleet Services', 'General Inquiry'];

  useEffect(() => {
    const scriptId = 'wf_script_home_zoho';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = scriptId;
    script.innerHTML = `
      function checkMandatory7155412000000630003() {
        var mndFileds = new Array('Last Name','Email','Phone');
        var fldLangVal = new Array('Last Name','Email','Phone');
        for(var i=0;i<mndFileds.length;i++) {
          var fieldObj=document.forms['WebToLeads7155412000000630003'][mndFileds[i]];
          if(fieldObj) {
            if (((fieldObj.value).replace(/^\\s+|\\s+$/g, '')).length==0) {
              alert(fldLangVal[i] +' cannot be empty.');
              fieldObj.focus();
              return false;
            }
          }
        }
        if(!validateEmail7155412000000630003()){return false;}
        document.querySelector('.crmWebToEntityForm .formsubmit').setAttribute('disabled', true);
        return true;
      }
      function validateEmail7155412000000630003() {
        var form = document.forms['WebToLeads7155412000000630003'];
        var emailFld = form.querySelectorAll('[ftype=email]');
        for(var i=0;i<emailFld.length;i++){
          var emailVal=emailFld[i].value;
          if((emailVal.replace(/^\\s+|\\s+$/g,'')).length!=0){
            var atpos=emailVal.indexOf('@');
            var dotpos=emailVal.lastIndexOf('.');
            if(atpos<1||dotpos<atpos+2||dotpos+2>=emailVal.length){
              alert('Please enter a valid email address.');
              emailFld[i].focus();
              return false;
            }
          }
        }
        return true;
      }
    `;
    document.body.appendChild(script);
    return () => { const s = document.getElementById(scriptId); if (s) s.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* ── HERO ── */}
      <section className="relative pt-[104px]">
        <div className="relative h-[480px] md:h-[560px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/fe9d5c1fa4085796084b26d0770bbf44.jpg"
            alt="Nevada desert highway"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/75"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            {/* 3rd party badge */}
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-6 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Licensed 3rd Party Nevada DMV Provider — Not the Government DMV</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4 max-w-4xl">
              Las Vegas Nevada DMV Services —<br />
              <span className="text-[#F5A623]">Licensed 3rd Party Provider</span>
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mb-8 leading-relaxed">
              Fast, professional Nevada DMV services — vehicle registration, title transfers, license plates &amp; more. Most transactions completed in under 30 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
                <i className="fas fa-phone text-sm"></i>
                <span>Call Now: 702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── THIRD PARTY NOTICE ── */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0 mt-0.5"></i>
            <p className="text-sm text-[#7a5c00] leading-relaxed">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party service provider. We assist clients with Nevada DMV transactions for a service fee. For official government DMV services, visit{' '}
              <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold hover:text-[#003366]">dmv.nv.gov</a>.
            </p>
          </div>
        </div>
      </div>

      {/* ── QUICK STATS BAR ── */}
      <div className="bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {[
              { value: '5,000+', label: 'Transactions Processed' },
              { value: '30 Min', label: 'Average Processing Time' },
              { value: '4.9 ★', label: 'Customer Rating' },
              { value: '10+ Yrs', label: 'Serving Las Vegas' },
            ].map((s, i) => (
              <div key={i} className="py-5 px-6 text-center">
                <p className="text-[#F5A623] text-2xl font-bold">{s.value}</p>
                <p className="text-white/70 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT: Go Online / Services Accordion ── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Complete DMV Services in Las Vegas
          </h2>
          <p className="text-gray-600 mb-8 pl-5">
            As a licensed 3rd party Nevada DMV provider, we handle all your DMV transactions — fast, professional, and without the long government lines.
          </p>

          {/* 2-column accordion grid */}
          <div className="grid md:grid-cols-2 gap-0 border border-gray-200 rounded-lg overflow-hidden">
            {SERVICES_ACCORDION.map((section, i) => (
              <div key={i} className={`border-b border-gray-200 ${i % 2 === 0 ? 'md:border-r' : ''} last:border-b-0`}>
                <button
                  onClick={() => toggleAccordion(i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="font-bold text-[#003366] text-sm">{section.title}</span>
                  <i className={`${openAccordions.includes(i) ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#F5A623] text-lg flex-shrink-0`}></i>
                </button>
                {openAccordions.includes(i) && (
                  <div className="px-5 pb-4 bg-gray-50 border-t border-gray-100">
                    <ul className="space-y-2 mt-3 mb-3">
                      {section.items.map((item, j) => (
                        <li key={j}>
                          <Link to={item.to} className="flex items-center space-x-2 text-sm text-gray-700 hover:text-[#003366] transition-colors group">
                            <i className="ri-arrow-right-s-line text-[#F5A623]"></i>
                            <span className="group-hover:underline">{item.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link to={section.link.to} className="inline-flex items-center space-x-1 text-[#003366] text-xs font-bold hover:text-[#F5A623] transition-colors mt-1">
                      <span>{section.link.label}</span>
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAKE AN APPOINTMENT ── */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Appointment info */}
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
                Make An Appointment
              </h2>
              <div className="pl-5">
                <p className="text-gray-600 mb-5 leading-relaxed">
                  Book your appointment at our Las Vegas office. Most transactions are completed in under 30 minutes — no long government lines, no waiting.
                </p>
                <div className="bg-[#FFF8E7] border border-[#F5A623] rounded p-4 mb-5">
                  <p className="text-sm text-[#7a5c00] font-semibold flex items-center space-x-2 mb-1">
                    <i className="fas fa-exclamation-triangle text-[#F5A623]"></i>
                    <span>Appointments Required</span>
                  </p>
                  <p className="text-sm text-[#7a5c00]">
                    Please call <a href="tel:7029797202" className="font-bold underline">702.979.7202</a> or book online before visiting. Walk-ins may not be accommodated.
                  </p>
                </div>
                <div className="space-y-3 mb-6">
                  {[
                    { icon: 'fas fa-map-marker-alt', label: 'Address', value: '4525 S Sandhill Rd, Las Vegas, NV 89121' },
                    { icon: 'fas fa-clock', label: 'Hours', value: 'Mon–Fri: 9:00 AM – 5:00 PM · Sat: 10:00 AM – 3:00 PM' },
                    { icon: 'fas fa-phone', label: 'Phone', value: '702.979.7202', href: 'tel:7029797202' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#003366] rounded flex-shrink-0">
                        <i className={`${item.icon} text-[#F5A623] text-xs`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} className="text-[#003366] font-semibold text-sm hover:underline">{item.value}</a>
                        ) : (
                          <p className="text-gray-700 text-sm">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-6 py-3 font-bold text-sm transition-colors whitespace-nowrap rounded">
                    <i className="ri-calendar-check-line"></i>
                    <span>Book Appointment</span>
                  </Link>
                  <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-6 py-3 font-bold text-sm transition-colors whitespace-nowrap rounded">
                    <i className="fas fa-phone text-xs"></i>
                    <span>Call Now</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Contact form */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <div className="bg-[#003366] px-6 py-4">
                <h3 className="text-white font-bold text-base">Free Consultation — Get a Quote</h3>
                <p className="text-[#F5A623] text-xs mt-0.5">We respond within 2 hours</p>
              </div>
              <div className="p-6">
                <div id="crmWebToEntityForm" className="zcwf_lblLeft crmWebToEntityForm">
                  <form
                    id="webform7155412000000630003"
                    action="https://crm.zoho.com/crm/WebToLeadForm"
                    name="WebToLeads7155412000000630003"
                    method="POST"
                    onSubmit={() => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const fn = (window as any).checkMandatory7155412000000630003;
                      return typeof fn === 'function' ? fn() : true;
                    }}
                    acceptCharset="UTF-8"
                  >
                    <input type="text" style={{ display: 'none' }} name="xnQsjsdp" value="888e4a72d32be66c0a9b89a7dc4e8bb21f37ec81fb5cbf430d09de8fec559fe7" readOnly />
                    <input type="hidden" name="zc_gad" id="zc_gad" value="" />
                    <input type="text" style={{ display: 'none' }} name="xmIwtLD" value="aad99b585fb71589a13576818e6b68f256c6a3fff07200e07f53beddb812aa5cf2c800cd33a427abaa20bbc18dea4bde" readOnly />
                    <input type="text" style={{ display: 'none' }} name="actionType" value="TGVhZHM=" readOnly />
                    <input type="text" style={{ display: 'none' }} name="returnURL" value="null" readOnly />

                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Full Name *</label>
                        <input type="text" name="Last Name" maxLength={80} required placeholder="Your full name"
                          className="w-full px-3 py-2.5 border border-gray-300 focus:border-[#003366] rounded text-gray-800 placeholder-gray-400 text-sm outline-none transition-colors" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Email *</label>
                          <input type="email" data-ftype="email" name="Email" maxLength={100} required placeholder="your@email.com"
                            className="w-full px-3 py-2.5 border border-gray-300 focus:border-[#003366] rounded text-gray-800 placeholder-gray-400 text-sm outline-none transition-colors" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Phone *</label>
                          <input type="tel" name="Phone" maxLength={30} required placeholder="702-000-0000"
                            className="w-full px-3 py-2.5 border border-gray-300 focus:border-[#003366] rounded text-gray-800 placeholder-gray-400 text-sm outline-none transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Service Needed</label>
                        <select name="LEADCF1" className="w-full px-3 py-2.5 border border-gray-300 focus:border-[#003366] rounded text-gray-800 text-sm outline-none transition-colors cursor-pointer bg-white">
                          <option value="-None-">Select a service...</option>
                          {zohoServices.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Message</label>
                        <textarea name="Description" maxLength={500} rows={3} placeholder="Tell us about your Nevada DMV needs..."
                          className="w-full px-3 py-2.5 border border-gray-300 focus:border-[#003366] rounded text-gray-800 placeholder-gray-400 text-sm outline-none transition-colors resize-none"></textarea>
                      </div>
                      <button type="submit" className="formsubmit w-full py-3 bg-[#003366] hover:bg-[#002244] text-white font-bold rounded transition-colors whitespace-nowrap cursor-pointer text-sm">
                        Send Message — It&apos;s Free
                      </button>
                      <p className="text-center text-xs text-gray-400 flex items-center justify-center space-x-1">
                        <i className="ri-lock-line"></i>
                        <span>No spam. We only contact you about your DMV needs.</span>
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HAVE YOUR DOCUMENTS READY ── */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Have Your Documents Ready
          </h2>
          <div className="pl-5">
            <p className="text-gray-600 mb-6 leading-relaxed">
              Want faster service when you arrive? Have your paperwork filled out before you get here! All Nevada DMV forms are available in our{' '}
              <Link to="/nevada-dmv-forms" className="text-[#003366] font-semibold underline hover:text-[#F5A623]">forms directory</Link>.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: 'ri-car-line', title: 'Vehicle Registration', desc: 'Title, proof of insurance, odometer disclosure', to: '/vehicle-registration' },
                { icon: 'ri-file-transfer-line', title: 'Title Transfer', desc: 'Signed title, bill of sale, ID, lien info', to: '/title-transfer' },
                { icon: 'ri-file-list-3-line', title: 'DMV Forms', desc: 'All Nevada DMV forms in one place', to: '/nevada-dmv-forms' },
                { icon: 'ri-file-text-line', title: 'Required Documents', desc: 'Full checklist for every transaction type', to: '/required-documents' },
              ].map((card, i) => (
                <Link key={i} to={card.to} className="group flex items-start space-x-3 p-4 border border-gray-200 rounded hover:border-[#003366] hover:bg-blue-50 transition-all">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#003366] rounded flex-shrink-0">
                    <i className={`${card.icon} text-[#F5A623] text-base`}></i>
                  </div>
                  <div>
                    <p className="font-bold text-[#003366] text-sm group-hover:underline">{card.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{card.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT CAN WE HELP YOU WITH (navy wave section) ── */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">What Can We Help You With?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOPIC_CARDS.map((card, i) => (
              <div key={i} className="bg-white/10 border border-white/20 rounded-lg p-6 hover:bg-white/15 transition-colors">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F5A623] rounded flex-shrink-0">
                    <i className={`${card.icon} text-white text-base`}></i>
                  </div>
                  <h3 className="font-bold text-white text-base">{card.title}</h3>
                </div>
                <p className="text-white/75 text-sm leading-relaxed mb-3">{card.desc}</p>
                <ul className="space-y-1 mb-4">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-center space-x-2 text-white/80 text-xs">
                      <i className="ri-arrow-right-s-line text-[#F5A623]"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to={card.link.to} className="inline-flex items-center space-x-1 text-[#F5A623] text-xs font-bold hover:text-yellow-300 transition-colors">
                  <span>{card.link.label}</span>
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY SPEEDY DMV vs GOVERNMENT DMV ── */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Nevada DMV vs. Speedy DMV Services
          </h2>
          <p className="text-gray-600 mb-8 pl-5">The difference is clear — see why thousands of Las Vegas drivers choose us.</p>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-3 bg-[#003366] text-white text-xs font-bold uppercase tracking-wide">
              <div className="px-5 py-3">Feature</div>
              <div className="px-5 py-3 border-l border-white/20">Nevada DMV Office</div>
              <div className="px-5 py-3 border-l border-white/20 bg-[#F5A623] text-white">Speedy DMV Services</div>
            </div>
            {[
              ['Average Wait Time', '2–4 Hours', '30 Minutes'],
              ['Appointment Availability', '3–6 Weeks Out', 'Same Week'],
              ['Paperwork Assistance', 'Self-Service Only', 'We Handle Everything'],
              ['Parking', 'Limited / Paid', 'Free & Easy'],
              ['Personal Guidance', 'General Info Only', 'One-on-One Expert Help'],
              ['Processing Speed', 'Standard Government', 'Expedited Service'],
            ].map(([feat, dmv, us], i) => (
              <div key={i} className={`grid grid-cols-3 border-t border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <div className="px-5 py-3 text-sm font-medium text-gray-700">{feat}</div>
                <div className="px-5 py-3 border-l border-gray-200">
                  <span className="flex items-center space-x-1.5 text-gray-500 text-sm">
                    <i className="ri-close-circle-line text-red-400"></i>
                    <span>{dmv}</span>
                  </span>
                </div>
                <div className="px-5 py-3 border-l border-gray-200 bg-[#FFF8E7]">
                  <span className="flex items-center space-x-1.5 text-[#003366] text-sm font-semibold">
                    <i className="ri-checkbox-circle-fill text-green-500"></i>
                    <span>{us}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <a href="tel:7029797202" className="inline-flex items-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call Now — Skip the Line: 702.979.7202</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            How It Works
          </h2>
          <p className="text-gray-600 mb-10 pl-5">Most customers are in and out in under 30 minutes. Here&apos;s how easy it is:</p>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-[#003366]/20"></div>
            {[
              { step: '01', icon: 'fas fa-phone', title: 'Call & Schedule', desc: 'Call 702.979.7202 to schedule your appointment and confirm what documents to bring.' },
              { step: '02', icon: 'ri-file-list-3-line', title: 'Prepare Documents', desc: 'We provide a clear checklist of exactly what you need for your specific transaction.' },
              { step: '03', icon: 'fas fa-map-marker-alt', title: 'Visit Our Office', desc: 'Come to 4525 S Sandhill Rd at your appointment time. No long lines, no waiting.' },
              { step: '04', icon: 'ri-check-double-line', title: 'Done & Driving!', desc: 'Your Nevada DMV transaction is complete. Back on the road in minutes, not hours.' },
            ].map((item, i) => (
              <div key={i} className="text-center relative">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#003366] rounded-full mb-4 relative z-10">
                  <i className={`${item.icon} text-[#F5A623] text-2xl`}></i>
                </div>
                <span className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">Step {item.step}</span>
                <h3 className="font-bold text-[#003366] text-base mt-1 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            What Las Vegas Drivers Say
          </h2>
          <p className="text-gray-600 mb-8 pl-5">Thousands of satisfied customers across Clark County, Nevada.</p>
          <div className="bg-[#003366] rounded-lg p-8 relative overflow-hidden mb-6">
            <div className="absolute top-6 left-6 text-white/5">
              <i className="fas fa-quote-left text-8xl"></i>
            </div>
            <div className="relative z-10">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => <i key={i} className="fas fa-star text-[#F5A623] text-sm mr-0.5"></i>)}
                <span className="text-white/50 text-sm ml-2">5.0 / 5.0</span>
              </div>
              <p className="text-white text-lg leading-relaxed mb-6 italic min-h-[72px]">
                &ldquo;{TESTIMONIALS[activeTestimonial].text}&rdquo;
              </p>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F5A623] rounded-full">
                    <i className="fas fa-user text-white text-sm"></i>
                  </div>
                  <div>
                    <p className="text-[#F5A623] font-bold text-sm">{TESTIMONIALS[activeTestimonial].name}</p>
                    <p className="text-white/60 text-xs">{TESTIMONIALS[activeTestimonial].location}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {TESTIMONIALS.map((_, i) => (
                    <button key={i} onClick={() => setActiveTestimonial(i)}
                      className={`rounded-full transition-all cursor-pointer ${i === activeTestimonial ? 'bg-[#F5A623] w-6 h-2.5' : 'bg-white/30 hover:bg-white/50 w-2.5 h-2.5'}`}></button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {[
              { icon: 'ri-google-fill', label: 'Google', rating: '4.9★', count: '127+ Reviews' },
              { icon: 'ri-star-fill', label: 'Yelp', rating: '4.8★', count: '84+ Reviews' },
              { icon: 'ri-facebook-fill', label: 'Facebook', rating: '5.0★', count: '60+ Reviews' },
            ].map((b, i) => (
              <div key={i} className="flex items-center space-x-3 bg-gray-50 border border-gray-200 rounded px-5 py-3">
                <i className={`${b.icon} text-[#003366] text-xl`}></i>
                <div>
                  <p className="text-[#003366] font-bold text-sm">{b.rating} <span className="text-gray-400 font-normal text-xs">{b.count}</span></p>
                  <p className="text-gray-400 text-xs">{b.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCATION ── */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Visit Our Las Vegas Office
          </h2>
          <p className="text-gray-600 mb-8 pl-5">Conveniently located in Las Vegas, serving all of Clark County, Nevada.</p>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white border border-gray-200 rounded-lg p-7">
              <div className="space-y-4 mb-6">
                {[
                  { icon: 'fas fa-map-marker-alt', label: 'Address', content: '4525 S Sandhill Rd, Las Vegas, NV 89121', href: 'https://maps.google.com?q=4525+S+Sandhill+Rd+Las+Vegas+NV' },
                  { icon: 'fas fa-phone', label: 'Phone', content: '702.979.7202', href: 'tel:7029797202' },
                  { icon: 'fas fa-clock', label: 'Hours', content: 'Mon–Fri: 9AM–5PM · Sat: 10AM–3PM · Sun: Closed' },
                  { icon: 'ri-map-pin-2-line', label: 'Service Area', content: 'All of Clark County, Nevada' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#003366] rounded flex-shrink-0">
                      <i className={`${item.icon} text-[#F5A623] text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs font-bold uppercase tracking-wide">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-[#003366] hover:underline font-semibold text-sm" target={item.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{item.content}</a>
                      ) : (
                        <p className="text-gray-700 text-sm">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <a href="tel:7029797202" className="flex-1 flex items-center justify-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white py-3 rounded font-bold text-sm transition-colors whitespace-nowrap">
                  <i className="fas fa-phone text-xs"></i><span>Call Now</span>
                </a>
                <Link to="/contact" className="flex-1 flex items-center justify-center space-x-2 border border-[#003366] text-[#003366] py-3 rounded font-semibold text-sm hover:bg-blue-50 transition-colors whitespace-nowrap">
                  <i className="ri-map-pin-line"></i><span>Get Directions</span>
                </Link>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-200 min-h-[360px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3223.8948108432234!2d-115.06516!3d36.08398!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c8c0b5c1234567%3A0x1234567890abcdef!2s4525%20S%20Sandhill%20Rd%2C%20Las%20Vegas%2C%20NV!5e0!3m2!1sen!2sus!4v1234567890123"
                width="100%" height="100%" style={{ border: 0, minHeight: '360px' }}
                allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                title="Speedy DMV Services Las Vegas Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-5 uppercase tracking-widest">
            <i className="fas fa-bolt"></i>
            <span>Skip the Line Today</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Skip the <span className="text-[#F5A623]">Nevada DMV?</span>
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            Call us now or book your appointment. Fast vehicle registration, title transfer, and more — handled by Las Vegas&apos; most trusted licensed 3rd party DMV service team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-9 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-9 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {['Licensed 3rd Party NV DMV Provider', 'Bonded & Insured', 'Same-Day Processing', 'All Vehicle Types'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/70 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
